import { useMemo, useState } from "react";
import { describeError } from "@/api/client";
import type { Workspace } from "@/api/session";
import { useUpdateWorkspace } from "@/api/workspace";
import {
  Button,
  Form,
  FormActions,
  FormSection,
  InlineAlert,
  NumberField,
  UnsavedChangesGuard,
  useToast,
} from "@/components";
import { Decimal, fractionToPercent, percentToFraction, type DecimalString } from "@/lib/decimal";

interface BenchValues {
  tolerancePercent: DecimalString | null;
  toleranceGrams: DecimalString | null;
  autoAdvanceMs: DecimalString | null;
}

type BenchErrors = Partial<Record<keyof BenchValues, string>>;

function valuesFrom(workspace: Workspace): BenchValues {
  const balance = workspace.settings.balance;
  return {
    tolerancePercent: balance?.tolerance_rel ? fractionToPercent(balance.tolerance_rel) : null,
    toleranceGrams: balance?.tolerance_abs_g ?? null,
    autoAdvanceMs: balance?.auto_advance_ms !== undefined ? String(balance.auto_advance_ms) : null,
  };
}

function validate(values: BenchValues): BenchErrors {
  const errors: BenchErrors = {};
  const percent = Decimal.tryFrom(values.tolerancePercent);
  if (!percent || percent.compare("0") <= 0 || percent.compare("10") > 0)
    errors.tolerancePercent = "Enter a tolerance above 0 % and up to 10 %.";
  const grams = Decimal.tryFrom(values.toleranceGrams);
  if (!grams || grams.isNegative() || grams.compare("1") > 0)
    errors.toleranceGrams = "Enter a tolerance from 0 g to 1 g.";
  const delay = Decimal.tryFrom(values.autoAdvanceMs);
  if (!delay || delay.isNegative() || delay.compare("10000") > 0 || !delay.round(0).equals(delay)) {
    errors.autoAdvanceMs = "Enter whole milliseconds from 0 to 10,000 (0 turns auto-advance off).";
  }
  return errors;
}

/** Balance tolerances used by weigh plans and the bench screens. */
export function BenchSection({ workspace, canEdit }: { workspace: Workspace; canEdit: boolean }) {
  const initial = useMemo(() => valuesFrom(workspace), [workspace]);
  const [values, setValues] = useState(initial);
  const [errors, setErrors] = useState<BenchErrors>({});
  const update = useUpdateWorkspace();
  const toast = useToast();
  const dirty = (Object.keys(initial) as Array<keyof BenchValues>).some((key) => values[key] !== initial[key]);

  function handleSubmit() {
    const found = validate(values);
    setErrors(found);
    if (Object.keys(found).length || !values.tolerancePercent || !values.toleranceGrams || !values.autoAdvanceMs)
      return;
    update.mutate(
      {
        settings: {
          balance: {
            tolerance_rel: percentToFraction(values.tolerancePercent),
            tolerance_abs_g: Decimal.from(values.toleranceGrams).normalize().toString(),
            auto_advance_ms: Number(values.autoAdvanceMs),
          },
        },
      },
      {
        onSuccess: (saved) => {
          setValues(valuesFrom(saved));
          toast.success("Bench settings saved");
        },
      },
    );
  }

  return (
    <Form onSubmit={handleSubmit} aria-label="Bench settings">
      <UnsavedChangesGuard when={dirty && canEdit} subject="your bench changes" />
      {update.isError ? (
        <InlineAlert tone="danger" title="Couldn’t save the bench settings">
          {describeError(update.error)}
        </InlineAlert>
      ) : null}
      <FormSection
        title="Balance tolerance"
        description="A reading is in tolerance when it is within the larger of the two limits: the relative tolerance of the target, or the absolute tolerance in grams."
      >
        <NumberField
          label="Relative tolerance"
          unit="%"
          decimals={2}
          min={0}
          value={values.tolerancePercent}
          onValueChange={(value) => setValues((current) => ({ ...current, tolerancePercent: value }))}
          description="1 % of a 2.000 g target allows ±0.020 g."
          error={errors.tolerancePercent}
          disabled={!canEdit}
        />
        <NumberField
          label="Absolute tolerance"
          unit="g"
          step={0.001}
          min={0}
          value={values.toleranceGrams}
          onValueChange={(value) => setValues((current) => ({ ...current, toleranceGrams: value }))}
          description="The floor for small targets; 0.002 g suits a 1 mg balance."
          error={errors.toleranceGrams}
          disabled={!canEdit}
        />
        <NumberField
          label="Auto-advance after a stable reading"
          unit="ms"
          step={100}
          min={0}
          decimals={0}
          value={values.autoAdvanceMs}
          onValueChange={(value) => setValues((current) => ({ ...current, autoAdvanceMs: value }))}
          description="How long a stable, in-tolerance reading waits before moving to the next line. 0 turns it off."
          error={errors.autoAdvanceMs}
          disabled={!canEdit}
        />
      </FormSection>
      <FormActions>
        <Button
          type="submit"
          variant="primary"
          pending={update.isPending}
          pendingLabel="Saving…"
          disabled={!canEdit || !dirty}
        >
          Save bench settings
        </Button>
        {dirty ? (
          <Button variant="ghost" onClick={() => setValues(initial)}>
            Discard changes
          </Button>
        ) : null}
      </FormActions>
    </Form>
  );
}
